import React, { useId, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Info, Plus } from "lucide-react";
import { useTRPC } from "@/trpc";
import { trpcClient } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { queryClient } from "@/ajax/queryClient";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormItem, FormLabel } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { tusUploadFile } from "@/ajax/upload/tusUploadFile";
import { HiringProcessItemType } from "@api/shared-runtime/enums";
import {
  HiringProcessTemplateForm,
  HiringProcessTemplateFormValues,
} from "./template-form";

const scanTerminalStatuses = new Set(["clean", "quarantined"]);

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface HiringProcessTemplateSelectProps {
  /** Current value — null means "None", undefined means no selection yet */
  value: string | null | undefined;
  /** Called when the user selects a template. null = none selected */
  onChange: (templateId: string | null) => void;
  error?: string;
}

export function HiringProcessTemplateSelect({
  value,
  onChange,
  error,
}: HiringProcessTemplateSelectProps) {
  const trpc = useTRPC();
  const selectId = useId();
  const { selectedOrgBusinessUnit } = useOrganization();
  const buId = selectedOrgBusinessUnit.id;
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data: templates, isLoading } = useQuery(
    trpc.hiringProcess.list.queryOptions({
      organizationBusinessUnitId: buId,
    }),
  );

  const createMutation = useMutation(
    trpc.hiringProcess.create.mutationOptions(),
  );

  if (!selectedOrgBusinessUnit.enableHiringProcess) {
    return null;
  }

  const handleChange = (selectValue: string) => {
    onChange(selectValue === "__none__" ? null : selectValue);
  };

  const mapStagesInput = (stages: HiringProcessTemplateFormValues["stages"]) =>
    stages.map((stage) => ({
      name: stage.name,
      description: stage.description ?? null,
      sortOrder: stage.sortOrder,
      items: stage.items.map((item) => ({
        title: item.title,
        description: item.description ?? null,
        sortOrder: item.sortOrder,
        isRequired: item.isRequired,
        assigneeRole: item.assigneeRole,
        itemType: item.itemType ?? HiringProcessItemType.Task,
        formId: item.formId ?? null,
        richTextContent: item.richTextContent ?? null,
        fileUploadIds: item.fileUploadIds ?? [],
        newFiles: item.newFiles ?? [],
      })),
    }));

  const handleCreate = async ({
    values,
    localFiles,
  }: {
    values: HiringProcessTemplateFormValues;
    localFiles: Map<string, File[]>;
  }) => {
    let createdTemplateId: string | null = null;
    try {
      const result = await createMutation.mutateAsync({
        organizationBusinessUnitId: buId,
        name: values.name,
        description: values.description ?? null,
        isDefault: values.isDefault,
        stages: mapStagesInput(values.stages),
      });
      createdTemplateId = result.template.id;

      // Upload pending files via TUS
      if (result.pendingUploads.length > 0) {
        await Promise.all(
          result.pendingUploads.map(async (upload) => {
            const key = `${upload.stageIndex}-${upload.itemIndex}`;
            const files = localFiles.get(key) ?? [];
            const file = files[upload.fileIndex];
            if (!file) {
              return;
            }

            await tusUploadFile({
              file,
              bucketName: upload.bucketName,
              objectName: upload.storagePath,
              uploadToken: upload.uploadToken,
            });
          }),
        );

        await trpcClient.fileAttachment.confirmUploads.mutate({
          fileUploadIds: result.pendingUploads.map((u) => u.fileUploadId),
        });

        const fileUploadIds = result.pendingUploads.map((u) => u.fileUploadId);
        for (let attempt = 0; attempt < 20; attempt++) {
          const statuses =
            await trpcClient.fileAttachment.getUploadStatuses.query({
              fileUploadIds,
            });
          const rejected = statuses.find((s) => s.scanStatus === "quarantined");
          if (rejected) {
            throw new Error(
              `${rejected.fileName} was removed by the security scan and was not saved to the hiring process.`,
            );
          }
          if (statuses.every((s) => s.scanStatus === "clean")) {
            break;
          }
          if (statuses.every((s) => scanTerminalStatuses.has(s.scanStatus))) {
            break;
          }
          await sleep(1500);
        }

        const finalStatuses =
          await trpcClient.fileAttachment.getUploadStatuses.query({
            fileUploadIds,
          });
        if (!finalStatuses.every((s) => s.scanStatus === "clean")) {
          throw new Error(
            "File security scan is still processing. The hiring process was not saved with that file. Try again after the scan finishes.",
          );
        }
      }

      await queryClient.invalidateQueries(
        trpc.hiringProcess.list.queryOptions({
          organizationBusinessUnitId: buId,
        }),
      );

      // Auto-select the newly created template
      onChange(result.template.id);
      setIsCreateOpen(false);
      toast.success("Hiring process template created");
    } catch (err) {
      if (createdTemplateId) {
        try {
          await trpcClient.hiringProcess.delete.mutate({
            organizationBusinessUnitId: buId,
            id: createdTemplateId,
          });
          await queryClient.invalidateQueries(
            trpc.hiringProcess.list.queryOptions({
              organizationBusinessUnitId: buId,
            }),
          );
        } catch {
          // Preserve the scan/upload error as the actionable message.
        }
      }
      toast.error(
        err instanceof Error ? err.message : "Failed to create template",
      );
    }
  };

  return (
    <>
      <FormItem>
        <div className="flex items-center gap-1.5">
          <FormLabel htmlFor={selectId} className="font-medium">
            Hiring Process
          </FormLabel>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Learn about hiring processes"
              >
                <Info size={14} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-80 max-w-[calc(100vw-2rem)] rounded-2xl border-slate-200 text-sm"
              side="top"
            >
              <div className="space-y-2">
                <p className="font-medium text-gray-900">
                  How Hiring Processes Work
                </p>
                <p className="text-gray-600">
                  A hiring process is a checklist of steps that need to be
                  completed when onboarding a provider for this job. This can
                  include forms to fill out, documents to share, and tasks to
                  complete.
                </p>
                <p className="text-gray-600">
                  Once a provider applies, both you and the provider can track
                  progress through each step of the checklist.
                </p>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex gap-2">
          <Select
            value={value === null ? "__none__" : value}
            onValueChange={handleChange}
            disabled={isLoading}
          >
            <SelectTrigger
              id={selectId}
              className="min-w-0 flex-1 [&>span]:min-w-0 [&>span]:truncate [&>span]:text-left"
            >
              <SelectValue placeholder="Select hiring process" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {templates?.length ? (
                templates.map((template) => {
                  const stageCount = template.stages?.length ?? 0;
                  return (
                    <SelectItem key={template.id} value={template.id}>
                      <span className="flex items-center gap-1.5">
                        {template.name}
                        {template.isDefault && (
                          <span className="text-gray-400 text-xs">
                            (Default)
                          </span>
                        )}
                        <span className="text-gray-400 text-xs">
                          · {stageCount} stage{stageCount !== 1 ? "s" : ""}
                        </span>
                      </span>
                    </SelectItem>
                  );
                })
              ) : (
                <SelectItem value="__no_templates__" disabled>
                  No templates yet
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsCreateOpen(true)}
            className="shrink-0 gap-1 px-3"
          >
            <Plus size={14} />
            New
          </Button>
        </div>
        {error ? (
          <p className="text-sm font-medium text-destructive">{error}</p>
        ) : null}
      </FormItem>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-4xl w-[calc(100vw-2rem)] sm:max-h-[90vh] overflow-y-auto !p-0">
          <DialogHeader className="mb-4 pt-6 px-6">
            <DialogTitle>Create Hiring Process Template</DialogTitle>
          </DialogHeader>
          <HiringProcessTemplateForm
            onSubmit={handleCreate}
            onCancel={() => setIsCreateOpen(false)}
            isSubmitting={createMutation.isPending}
            error={createMutation.error?.message}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
